import { IWPImage } from "@/src/types";
import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import S from "./CasesBlock.styled";
import { MainInner } from "@/styles/global";
import { AnimationOnScroll } from "react-animation-on-scroll";

export interface Props {
  animation: string;
  title: string;
  cases: {
    categories: {
      nodes: {
        name: string;
      }[];
    };
    featuredMediaSelect: {
      featuredMediaSelect: "image" | "video";
      featuredVideo: string;
    };
    featuredImage: {
      node: IWPImage;
    };
    title: string;
    uri: string;
  }[];
}

const CasesBlock: React.FC<Props> = ({ animation, title, cases }) => {
  const [showFilter, setShowFilter] = React.useState(false);
  const [activeFilter, setActiveFilter] = React.useState("all");

  const [limitedCases, setLimitedCases] = React.useState(cases);
  const [filteredCases, setFilteredCases] = React.useState(limitedCases);
  const [showMore, setShowMore] = React.useState(10);

  const handleShowMore = () => {
    setShowMore((prev) => prev + 10);
  };

  const renderCases = filteredCases?.map((caseItem, i) => {
    const { featuredImage, title, uri, featuredMediaSelect } = caseItem;
    const caseImageData = featuredImage?.node?.localFile?.childImageSharp
      ?.gatsbyImageData
      ? getImage(featuredImage.node.localFile.childImageSharp.gatsbyImageData)
      : null;

    // split title into two at the first .
    const splitTitle = title.split(".");
    const firstTitle = splitTitle[0];
    const secondTitle = splitTitle.slice(1).join(".");
    return (
      <S.CardLink to={uri} key={i}>
        {caseImageData && featuredMediaSelect.featuredMediaSelect === "image" && (
          <AnimationOnScroll
            animateIn={`animate__${animation}`}
            animateOnce={true}
            initiallyVisible={animation == "none" ? true : false}
            duration={animation === "none" ? 0 : 1}
            delay={animation === "none" ? 0 : 100}
          >
            <S.ImageWrapper>
              <S.CardImage image={caseImageData} alt="" />
            </S.ImageWrapper>
          </AnimationOnScroll>
        )}
        {featuredMediaSelect.featuredMediaSelect === "video" &&
          featuredMediaSelect.featuredVideo && (
            <AnimationOnScroll
              animateIn={`animate__${animation}`}
              animateOnce={true}
              initiallyVisible={animation == "none" ? true : false}
              duration={animation === "none" ? 0 : 1}
              delay={animation === "none" ? 0 : 100}
            >
              <S.ImageWrapper>
                {caseImageData && <S.CardImage image={caseImageData} alt="" />}
                <iframe
                  src={`https://player.vimeo.com/video/${featuredMediaSelect.featuredVideo}?h=bc7bf21e48&autoplay=1&loop=1&title=0&byline=0&portrait=0&muted=1&autopause=0&controls=0`}
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                ></iframe>
              </S.ImageWrapper>
            </AnimationOnScroll>
          )}
        <S.TitlesWrapper>
          {firstTitle && (
            <S.FirstTitle>
              {firstTitle}
              {". "}
            </S.FirstTitle>
          )}
          {secondTitle && <S.SecondTitle>{secondTitle}</S.SecondTitle>}
        </S.TitlesWrapper>
      </S.CardLink>
    );
  });

  React.useEffect(() => {
    if (activeFilter === "all") {
      setFilteredCases(limitedCases);
    } else if (activeFilter === "film") {
      // filter case by film category in andy position in the array
      setFilteredCases(
        limitedCases.filter((caseItem) =>
          caseItem.categories.nodes.some((category) => category.name === "Film")
        )
      );
    } else if (activeFilter === "design") {
      setFilteredCases(
        limitedCases.filter((caseItem) =>
          caseItem.categories.nodes.some(
            (category) => category.name === "Design"
          )
        )
      );
    } else if (activeFilter === "event") {
      setFilteredCases(
        limitedCases.filter((caseItem) =>
          caseItem.categories.nodes.some(
            (category) => category.name === "Event"
          )
        )
      );
    } else if (activeFilter === "advertising") {
      setFilteredCases(
        limitedCases.filter((caseItem) =>
          caseItem.categories.nodes.some(
            (category) => category.name === "Advertising"
          )
        )
      );
    }

    return;
  }, [activeFilter, limitedCases]);

  return (
    <MainInner>
      <S.Container>
        <S.FilterWrapper active={showFilter}>
          <S.Filter
            active={showFilter}
            onClick={() => setShowFilter(!showFilter)}
          >
            <span>Filter</span>
            <span>
              <div className="line" />
              <div className="line standing" />
            </span>
          </S.Filter>
          <S.FilterOptions active={showFilter}>
            <S.FilterOption
              onClick={() => setActiveFilter("all")}
              active={activeFilter === "all"}
            >
              All
            </S.FilterOption>
            <S.FilterOption
              onClick={() => setActiveFilter("film")}
              active={activeFilter === "film"}
            >
              Film
            </S.FilterOption>
            <S.FilterOption
              onClick={() => setActiveFilter("design")}
              active={activeFilter === "design"}
            >
              Design
            </S.FilterOption>
            <S.FilterOption
              onClick={() => setActiveFilter("event")}
              active={activeFilter === "event"}
            >
              Event
            </S.FilterOption>
            <S.FilterOption
              onClick={() => setActiveFilter("advertising")}
              active={activeFilter === "advertising"}
            >
              Advertising
            </S.FilterOption>
          </S.FilterOptions>
        </S.FilterWrapper>
        <S.Grid show={showMore}>{renderCases}</S.Grid>
        {showMore < filteredCases.length && (
          <S.LoadMoreButton onClick={handleShowMore}>More +</S.LoadMoreButton>
        )}
      </S.Container>
    </MainInner>
  );
};

export default CasesBlock;
